/**
 * Cursor tooltip (blob `updateTooltip`). Writes into the
 * `#uplot-tooltip` node `<plot-chart>` handed to `runtime`. Rebuilds its HTML
 * only when the nearest-point index changes (dense plots fire setCursor ~60/s),
 * then `placeTooltip` puts it where this person chose.
 */

import type uPlot from 'uplot';
import { runtime } from '../plot-runtime.ts';
import { plotState } from '../../../state/plot-state.ts';
import { PLOT_COLORS } from '../plot-constants.ts';
import { formatNum, parseLegSec, fmtTimeFull, escHtml } from './format.ts';
import { changeoverColor, changeoverTypeDisplay, formatChangeoverDuration } from './overlays.ts';
import type { AnyPlotResponse, Changeover, Leg } from '../../../api/types.ts';
import { isXY } from '../../../api/plot-shapes.ts';
import { tooltipPosition } from '../../../state/hover-tooltips.ts';

export interface TooltipExtra {
  isHist?: boolean;
  edges?: number[];
  /** Period identity plus the immutable UTC origin for elapsed display. */
  periodLabel?: string;
  periodStartEpoch?: number;
  periodEndEpoch?: number;
  periodLegs?: Leg[];
  periodChangeovers?: Changeover[];
}

function elapsedLabel(seconds: number): string {
  const sign = seconds < 0 ? '-' : '';
  const total = Math.abs(Math.round(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const rest = total % 60;
  return `${sign}${hours}h ${String(minutes).padStart(2, '0')}m ${String(rest).padStart(2, '0')}s`;
}

interface SeriesOpt {
  label?: string;
  stroke?: unknown;
}

export function hideTooltip(): void {
  runtime.tooltipEl?.classList.remove('visible');
}

/**
 * Where a tooltip should be written and positioned. Grid view passes its own
 * per-panel node so every panel can show a reading at the synced cursor at the
 * same time; the single chart falls back to the one node `<plot-chart>` owns.
 */
export interface TooltipTarget {
  el: HTMLElement;
  container: HTMLElement;
}

export function updateTooltip(
  u: uPlot,
  data: AnyPlotResponse,
  seriesOpts: SeriesOpt[],
  extra?: TooltipExtra,
  target?: TooltipTarget,
): void {
  const tt = target?.el ?? runtime.tooltipEl;
  if (!tt) return;
  // Memoized per node rather than in one shared field: with a tooltip per grid
  // panel, a single `lastTtIdx` would let one panel's index suppress another
  // panel's rebuild and leave it showing the wrong ship's values.
  //
  // The key carries the visible-series set as well as the index, so toggling a
  // legend row while parked on one point rebuilds instead of leaving the hidden
  // series listed.
  const lastIdx = tt.dataset.ttIdx ?? null;
  const idx = u.cursor.idx;
  if (idx == null || !u.over) {
    tt.classList.remove('visible');
    delete tt.dataset.ttIdx;
    return;
  }
  if (extra?.periodStartEpoch != null && extra.periodEndEpoch != null) {
    const cursorX = u.posToVal(u.cursor.left ?? 0, 'x');
    const duration = extra.periodEndEpoch - extra.periodStartEpoch;
    if (!Number.isFinite(cursorX) || cursorX < 0 || cursorX >= duration) {
      tt.classList.remove('visible');
      delete tt.dataset.ttIdx;
      return;
    }
  }
  const xVal = (u.data[0] as Array<number | null>)[idx];
  if (xVal == null) {
    tt.classList.remove('visible');
    runtime.lastTtIdx = null;
    return;
  }

  const isTime = isXY(data) && data.x_is_time;
  const tooltipTimestamp = extra?.periodStartEpoch != null ? extra.periodStartEpoch + xVal : xVal;
  const xColumn =
    isXY(data) ? data.x_column : 'x';
  const s = plotState.value;

  const memoKey = `${idx}:${u.series.map((sr) => (sr.show === false ? '0' : '1')).join('')}`;
  if (memoKey !== lastIdx) {
    const parts: string[] = [];
    if (extra?.periodLabel) {
      parts.push(`<div class="uptt-period">${escHtml(extra.periodLabel)}</div>`);
    }
    if (extra?.isHist) {
      const edges = extra.edges || [];
      const lo = edges[idx]!;
      const hi = edges[idx + 1]!;
      const mid = (lo + hi) / 2;
      parts.push(`<div class="uptt-x">[${formatNum(lo)}, ${formatNum(hi)}) &middot; mid ${formatNum(mid)}</div>`);
    } else if (extra?.periodStartEpoch != null) {
      const timestamp = extra.periodStartEpoch + xVal;
      parts.push(
        `<div class="uptt-x">${fmtTimeFull(timestamp)} · +${escHtml(elapsedLabel(xVal))}</div>`,
      );
    } else if (isTime) {
      parts.push(`<div class="uptt-x">${fmtTimeFull(xVal)}</div>`);
    } else {
      parts.push(`<div class="uptt-x">${escHtml(xColumn || 'x')} = ${formatNum(xVal)}</div>`);
    }

    for (let si = 1; si < u.data.length; si++) {
      // A series hidden from its legend row should not be read out here either.
      if (u.series[si]?.show === false) continue;
      const v = (u.data[si] as Array<number | null>)[idx];
      const opt = seriesOpts[si];
      const color =
        opt && typeof opt.stroke === 'string'
          ? opt.stroke
          : PLOT_COLORS[(si - 1) % PLOT_COLORS.length]!;
      const label = (opt && opt.label) || '';
      const val = v == null || !Number.isFinite(v) ? '-' : formatNum(v);
      parts.push(
        `<div class="uptt-row"><span class="uptt-swatch" style="background:${color}"></span>` +
          `<span class="uptt-name">${escHtml(label)}</span>` +
          `<span class="uptt-val">${val}</span></div>`,
      );
    }

    const tooltipLegs = extra?.periodLegs ?? runtime.lastLegs;
    if (s.show_legs && (isTime || extra?.periodStartEpoch != null) && tooltipLegs?.length) {
      for (const leg of tooltipLegs) {
        const startSec = parseLegSec(leg.start);
        const endSec = parseLegSec(leg.end);
        if (Number.isFinite(startSec) && Number.isFinite(endSec) && tooltipTimestamp >= startSec && tooltipTimestamp <= endSec) {
          parts.push(`<div class="uptt-leg">Leg <strong>${escHtml(String(leg.leg_key || ''))}</strong></div>`);
          break;
        }
      }
    }

    const tooltipChangeovers = extra?.periodChangeovers ?? runtime.lastChangeovers;
    if (s.show_changeovers && (isTime || extra?.periodStartEpoch != null) && tooltipChangeovers?.length) {
      for (const co of tooltipChangeovers) {
        const startSec = parseLegSec(co.start);
        const endSec = parseLegSec(co.end);
        if (!Number.isFinite(startSec) || !Number.isFinite(endSec)) continue;
        if (tooltipTimestamp < startSec || tooltipTimestamp > endSec) continue;
        const color = changeoverColor(co.type);
        const typeDisp = changeoverTypeDisplay(co.type);
        const fuelPart =
          co.source_fuel && co.target_fuel
            ? ` ${escHtml(co.source_fuel)}→${escHtml(co.target_fuel)}`
            : '';
        const dur = formatChangeoverDuration(startSec, endSec);
        parts.push(
          `<div class="uptt-row">` +
            `<span class="uptt-swatch" style="background:${color.solid}"></span>` +
            `<span class="uptt-name">${escHtml(co.engine || '')} · ${escHtml(typeDisp)}${fuelPart}</span>` +
            `<span class="uptt-val">${escHtml(dur)}</span>` +
            `</div>`,
        );
      }
    }

    tt.innerHTML = parts.join('');
    tt.dataset.ttIdx = memoKey;
  }
  tt.classList.add('visible');

  // Anchored to the instance's own root rather than `runtime.rootEl`: in grid
  // view the uPlot lives inside a panel, not the single-chart mount, and the
  // shared tooltip node has to follow whichever panel fired.
  const container = target?.container ?? runtime.containerEl;
  if (container) placeTooltip(u, tt, container);
}

/**
 * Put a visible tooltip where this person chose (`state/hover-tooltips.ts`):
 * pinned to a corner of the plot, or beside the pointer on a chosen side.
 *
 * Shared by every uPlot renderer. It never centres on the pointer and never
 * flips sides: a box that changes width or crosses the top edge used to jump,
 * so at an edge it stops against the edge instead.
 */
export function placeTooltip(u: uPlot, tt: HTMLElement, container: HTMLElement): void {
  const cont = container.getBoundingClientRect();
  const plot = u.over.getBoundingClientRect();
  const l = plot.left - cont.left;
  const t = plot.top - cont.top;
  const r = l + plot.width;
  const b = t + plot.height;
  const px = l + (u.cursor.left || 0);
  const py = t + (u.cursor.top || 0);
  const pos = tooltipPosition();
  const fixed = pos.mode === 'fixed';
  // Before measuring: the fixed box has a steady minimum width.
  tt.classList.toggle('tt-fixed', fixed);
  const w = tt.offsetWidth;
  const h = tt.offsetHeight;
  let left: number;
  let top: number;
  if (fixed) {
    left = pos.corner[1] === 'l' ? l + 8 : r - w - 8;
    top = pos.corner[0] === 't' ? t + 8 : b - h - 8;
  } else {
    left = pos.side[1] === 'l' ? px - 14 - w : px + 14;
    top = pos.side[0] === 't' ? py - 14 - h : py + 14;
  }
  // Inside the container either way - a small grid panel can be narrower than the box.
  left = Math.max(4, Math.min(left, cont.width - w - 4));
  top = Math.max(4, Math.min(top, cont.height - h - 4));
  const under =
    fixed && pos.fade && px >= left - 12 && px <= left + w + 12 && py >= top - 12 && py <= top + h + 12;
  tt.classList.toggle('tt-faded', under);
  tt.style.left = left + 'px';
  tt.style.top = top + 'px';
  tt.style.transform = 'none';
}
